import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, ChevronLeft, ExternalLink, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trackEvent } from '@/lib/analytics';
import { cardClass, eyebrowClass } from '@/lib/sectionStyles';
import { useLeadFormSubmit } from '@/hooks/useLeadFormSubmit';
import {
  type Answers,
  HERO_INTRO_COPY,
  META_DESCRIPTION_COPY,
  OG_DESCRIPTION_COPY,
  QUESTIONS,
  RISK_TIERS,
  type RiskTierId,
  classify,
  decodeAnswers,
  encodeAnswers,
  isComplete,
} from '@/lib/euAiActClassifier';

/**
 * G1.11 — free, ungated EU AI Act classifier (lead-gen entry point).
 *
 * Two surfaces on one component:
 *   - /tools/eu-ai-act-classifier                         — the questionnaire
 *   - /tools/eu-ai-act-classifier/result/:hash            — a shareable result
 *
 * The result is derived from the URL hash (a fixed-length base-3 encoding of
 * every answer) so sharing a link does not require any server-side state.
 */

const ANALYTICS_CATEGORY = 'eu_ai_act_classifier';

export function EuAiActClassifierPage() {
  const { hash } = useParams<{ hash?: string }>();
  const navigate = useNavigate();

  const initialAnswers = useMemo<Answers>(() => (hash ? decodeAnswers(hash) : {}), [hash]);
  const [answers, setAnswers] = useState<Answers>(initialAnswers);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasStartedEvent, setHasStartedEvent] = useState(false);

  // Show result view iff the URL carries a valid hash and every question is
  // answered — otherwise fall through to the questionnaire.
  const showResult = Boolean(hash) && isComplete(initialAnswers);

  useEffect(() => {
    if (showResult) {
      const tier = classify(initialAnswers);
      trackEvent(ANALYTICS_CATEGORY, 'result_view', tier);
    }
  }, [showResult, initialAnswers]);

  if (showResult) {
    return (
      <ResultView
        answers={initialAnswers}
        onRestart={() => navigate('/tools/eu-ai-act-classifier')}
      />
    );
  }

  const currentQuestion = QUESTIONS[currentIndex];
  const answered = answers[currentQuestion.id];
  const isLast = currentIndex === QUESTIONS.length - 1;

  const answerAndAdvance = (value: boolean) => {
    if (!hasStartedEvent) {
      trackEvent(ANALYTICS_CATEGORY, 'start', currentQuestion.id);
      setHasStartedEvent(true);
    }

    const next: Answers = { ...answers, [currentQuestion.id]: value };
    setAnswers(next);

    trackEvent(ANALYTICS_CATEGORY, 'answer', `${currentQuestion.id}:${value ? 'yes' : 'no'}`);

    if (isLast) {
      const tier = classify(next);
      trackEvent(ANALYTICS_CATEGORY, 'complete', tier);
      navigate(`/tools/eu-ai-act-classifier/result/${encodeAnswers(next)}`);
      return;
    }

    setCurrentIndex((idx) => idx + 1);
  };

  const goBack = () => {
    if (currentIndex === 0) return;
    setCurrentIndex((idx) => idx - 1);
  };

  return (
    <>
      <Helmet>
        <title>EU AI Act Risk Tier Classifier — Free Tool | CriticalBridge.AI</title>
        <meta name="description" content={META_DESCRIPTION_COPY} />
        <meta
          name="keywords"
          content="EU AI Act, risk tier, high-risk AI, Article 6, Annex III, GPAI, systemic risk, AI compliance, free tool"
        />
        <meta property="og:title" content="EU AI Act Risk Tier Classifier — Free Tool" />
        <meta property="og:description" content={OG_DESCRIPTION_COPY} />
        <link rel="canonical" href="https://www.criticalbridge.ai/tools/eu-ai-act-classifier" />
      </Helmet>

      <section className="bg-secondary">
        <div className="container mx-auto px-4 py-12 sm:py-16">
          <div className="font-mono text-xs text-secondary-foreground/60">
            <Link to="/" className="hover:text-secondary-foreground">
              Home
            </Link>{' '}
            / Tools / EU AI Act Classifier
          </div>
          <span className="mt-4 block font-mono text-xs font-semibold uppercase tracking-widest text-primary">
            FREE TOOL
          </span>
          <h1 className="mt-2 max-w-[24ch] text-3xl font-extrabold leading-tight text-secondary-foreground sm:text-4xl">
            Which EU AI Act risk tier applies to your system?
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-secondary-foreground/70">
            {HERO_INTRO_COPY}
          </p>
        </div>
      </section>

      <section className="py-12 sm:py-16">
        <div className="container mx-auto max-w-2xl px-4">
          <div
            className="mb-6 flex items-center justify-between"
            aria-label={`Question ${currentIndex + 1} of ${QUESTIONS.length}`}
          >
            <span className={eyebrowClass}>
              Question {currentIndex + 1} of {QUESTIONS.length}
            </span>
            <button
              type="button"
              onClick={goBack}
              disabled={currentIndex === 0}
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:hover:text-muted-foreground"
              aria-label="Go to previous question"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Back
            </button>
          </div>

          <div
            className="h-2 w-full rounded-full bg-muted"
            role="progressbar"
            aria-valuenow={currentIndex + 1}
            aria-valuemin={1}
            aria-valuemax={QUESTIONS.length}
          >
            <div
              className="h-2 rounded-full bg-primary transition-all"
              style={{ width: `${((currentIndex + 1) / QUESTIONS.length) * 100}%` }}
            />
          </div>

          <div className={`${cardClass} mt-8`}>
            <h2 className="text-xl font-bold leading-snug">{currentQuestion.prompt}</h2>
            <p className="mt-3 text-sm text-muted-foreground">{currentQuestion.helper}</p>

            <div className="mt-6 flex flex-wrap gap-3">
              <Button
                size="lg"
                onClick={() => answerAndAdvance(true)}
                aria-pressed={answered === true}
              >
                Yes
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => answerAndAdvance(false)}
                aria-pressed={answered === false}
              >
                No
              </Button>
            </div>
          </div>

          <p className="mt-6 text-xs text-muted-foreground">
            This tool provides an indicative classification based on the questions you answered. It
            is not legal advice. For a full assessment tied to your specific system, deployer role,
            and jurisdiction, use CriticalBridge to generate a documented, examiner-ready record.
          </p>
        </div>
      </section>
    </>
  );
}

interface ResultViewProps {
  answers: Answers;
  onRestart: () => void;
}

function ResultView({ answers, onRestart }: ResultViewProps) {
  const tierId: RiskTierId = classify(answers);
  const tier = RISK_TIERS[tierId];

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    trackEvent(ANALYTICS_CATEGORY, 'share_click', tierId);
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        // Clipboard write can fail (permissions, insecure context). Fall through
        // silently — the URL is still visible in the browser address bar.
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>{`${tier.label} — EU AI Act Classifier | CriticalBridge.AI`}</title>
        <meta
          name="description"
          content={`EU AI Act classification result: ${tier.label}. ${tier.tagline}`}
        />
        <meta property="og:title" content={`${tier.label} — EU AI Act Classifier`} />
        <meta property="og:description" content={tier.tagline} />
      </Helmet>

      <section className="bg-secondary">
        <div className="container mx-auto px-4 py-12 sm:py-16">
          <div className="font-mono text-xs text-secondary-foreground/60">
            <Link to="/" className="hover:text-secondary-foreground">
              Home
            </Link>{' '}
            /{' '}
            <Link to="/tools/eu-ai-act-classifier" className="hover:text-secondary-foreground">
              Tools
            </Link>{' '}
            / Result
          </div>
          <span className="mt-4 block font-mono text-xs font-semibold uppercase tracking-widest text-primary">
            YOUR RESULT
          </span>
          <h1 className="mt-2 max-w-[24ch] text-3xl font-extrabold leading-tight text-secondary-foreground sm:text-4xl">
            {tier.label}
          </h1>
          <p className="mt-3 max-w-2xl text-lg text-secondary-foreground/80">{tier.tagline}</p>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-secondary-foreground/70">
            {tier.summary}
          </p>
        </div>
      </section>

      <section className="py-12 sm:py-16">
        <div className="container mx-auto max-w-3xl px-4">
          <span className={eyebrowClass}>Key obligations</span>
          <h2 className="mt-2 text-2xl font-bold">What this means for you</h2>

          <ul className="mt-6 grid gap-3">
            {tier.obligations.map((item) => (
              <li key={item} className="flex items-start gap-3 rounded-lg border bg-card p-4">
                <CheckCircle2
                  className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary"
                  aria-hidden="true"
                />
                <span className="text-sm text-foreground">{item}</span>
              </li>
            ))}
          </ul>

          <p className="mt-6 text-xs text-muted-foreground">Reference: {tier.reference}</p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button size="lg" variant="outline" onClick={handleShare} aria-live="polite">
              <Share2 className="h-4 w-4" aria-hidden="true" />
              {copied ? 'Link copied' : 'Copy shareable link'}
            </Button>
            <Button size="lg" variant="ghost" onClick={onRestart}>
              Start over
            </Button>
          </div>
        </div>
      </section>

      <ReportCaptureBand tierId={tierId} />

      <section className="bg-secondary py-16">
        <div className="container mx-auto px-4">
          <h2 className="max-w-2xl text-2xl font-bold text-secondary-foreground sm:text-3xl">
            Turn this classification into examiner-ready evidence.
          </h2>
          <p className="mt-4 max-w-[68ch] text-secondary-foreground/70">
            CriticalBridge operationalises the EU AI Act as enforced record fields, not a marketing
            label — risk tier and jurisdiction on every governed agent, policy evaluated before
            every action, per-decision lineage with hash-chained audit, and one-click evidence pack
            export. See the platform in action, or start the Discovery Trial.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button
              size="lg"
              asChild
              onClick={() => trackEvent(ANALYTICS_CATEGORY, 'cta_trial_click', tierId)}
            >
              <Link to="/trial">Start the Discovery Trial</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-secondary-foreground/30 bg-transparent text-secondary-foreground hover:bg-secondary-foreground/10 hover:text-secondary-foreground"
              asChild
              onClick={() => trackEvent(ANALYTICS_CATEGORY, 'cta_lighthouse_click', tierId)}
            >
              <Link to="/lighthouse">
                See Agent Lighthouse
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}

interface ReportCaptureBandProps {
  tierId: RiskTierId;
}

function ReportCaptureBand({ tierId }: ReportCaptureBandProps) {
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const { isSubmitting, status, errorMessage, submit } = useLeadFormSubmit();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Attempt event is safe pre-await — useful for measuring form-abandonment
    // between "user clicked submit" and "backend accepted". The conversion
    // event below only fires on a successful backend accept so GA4 funnel
    // numbers match lead-store records. On thrown errors from `submit()` we
    // fire `report_capture_submit_error` and re-throw so infra failures are
    // distinguishable from silent user-abandonment in GA4 (#12258).
    trackEvent(ANALYTICS_CATEGORY, 'report_capture_submit_attempt', tierId);
    try {
      const ok = await submit({
        formType: 'waitlist',
        firstName,
        lastName,
        email,
        company,
        message: `EU AI Act classifier — requested full assessment report. Classified tier: ${tierId}.`,
        heardAbout: 'eu_ai_act_classifier',
      });
      if (ok) {
        trackEvent(ANALYTICS_CATEGORY, 'report_capture_submit', tierId);
      }
    } catch (err) {
      trackEvent(ANALYTICS_CATEGORY, 'report_capture_submit_error', tierId);
      throw err;
    }
  };

  if (status === 'success') {
    return (
      <section className="bg-muted py-12">
        <div className="container mx-auto max-w-3xl px-4 text-center">
          <span className={eyebrowClass}>Report on its way</span>
          <h2 className="mt-2 text-2xl font-bold">Thanks — we'll be in touch shortly.</h2>
          <p className="mt-3 text-muted-foreground">
            We'll send a tailored EU AI Act assessment for your system to <strong>{email}</strong>.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-muted py-12">
      <div className="container mx-auto max-w-3xl px-4">
        <span className={eyebrowClass}>Optional</span>
        <h2 className="mt-2 text-2xl font-bold">Get a full assessment report</h2>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          We'll email a tailored EU AI Act assessment for your specific system, including the
          Chapter III obligations that apply, deployer vs provider split, and a starter evidence
          checklist. No signup, no phone call required.
        </p>

        <form onSubmit={handleSubmit} className="mt-6 grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1 block font-medium">First name</span>
            <input
              type="text"
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              autoComplete="given-name"
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Last name</span>
            <input
              type="text"
              required
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              autoComplete="family-name"
            />
          </label>
          <label className="block text-sm sm:col-span-2">
            <span className="mb-1 block font-medium">Work email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              autoComplete="email"
            />
          </label>
          <label className="block text-sm sm:col-span-2">
            <span className="mb-1 block font-medium">Company (optional)</span>
            <input
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              autoComplete="organization"
            />
          </label>

          <div className="sm:col-span-2">
            <Button size="lg" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Sending…' : 'Email me the assessment'}
            </Button>
            {status === 'error' && (
              <p className="mt-3 text-sm text-destructive" role="alert">
                {errorMessage || 'Something went wrong. Please try again.'}
              </p>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}
